import { useCallback, useMemo, useState } from 'react';
import '@screentone/addon-autocomplete/css/index.css';
import { AutoComplete } from '@screentone/addon-autocomplete';
import { Button, FormLabel, Group, IconCrossCircle } from '@screentone/core';
import classnames from 'classnames';

import useEntitlementsData from 'hooks/useEntitlementsData';
import styles from './EntitlementsManager.module.scss';

interface EntitlementsManagerProps {
  value: string[];
  onChange: (entitlements: string[]) => void;
  staticPlacement?: boolean;
  isReadOnly?: boolean;
}

const EntitlementsManager = ({
  value,
  onChange,
  staticPlacement = false,
  isReadOnly = false
}: EntitlementsManagerProps) => {
  const [inputValue, setInputValue] = useState('');
  const { entitlements } = useEntitlementsData();

  const availableLabels = useMemo(() => entitlements.map((e) => e.label), [entitlements]);

  // Use labels for display
  const selectedLabels = useMemo(
    () => value.map((key) => entitlements.find((e) => e.key === key)?.label ?? key),
    [value, entitlements]
  );

  const handleSetTags = useCallback(
    (newLabels: string[]) => {
      // Convert labels back to keys
      const newKeys = newLabels.map((label) => entitlements.find((e) => e.label === label)?.key ?? label);
      onChange(newKeys);
    },
    [onChange, entitlements]
  );

  const handleClearAll = useCallback(() => {
    onChange([]);
  }, [onChange]);
  return (
    <>
      <FormLabel label="Entitlements" fullWidth margin={{ top: 'sm' }} />
      <Group gap="xs" className={styles.inputContainer}>
        <AutoComplete
          id="autocomplete-entitlements"
          dropDownClassName={classnames(styles.autocompleteDropdown, { [styles.staticPlacement]: staticPlacement })}
          dropDownItems={availableLabels
            .filter((label) => !selectedLabels.includes(label))
            .filter((label) => label.toLowerCase().includes(inputValue.toLocaleLowerCase()))}
          inputValue={inputValue}
          onInput={(input: string) => {
            setInputValue(input);
          }}
          onDismiss={handleSetTags}
          onSelect={handleSetTags}
          tags={selectedLabels}
          placeholder="Select entitlements to include or exclude from the update"
          className={styles.hideButton}
          disabled={isReadOnly}
        />
        {!isReadOnly && (
          <Button
            tertiary
            type="button"
            icon={IconCrossCircle}
            className={styles.deleteIcon}
            onClick={handleClearAll}
          />
        )}
      </Group>
    </>
  );
};

export default EntitlementsManager;
